// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import 'myWorld/base/view';

myw.Dialog = myw.View.extend({
    options: {
        modal: true,
        autoOpen: true,
        width: 'auto',
        resizable: false,
        destroyOnClose: false,
        closeText: '{:close_tooltip}',
        buttons: {
            Close: {
                text: '{:close_btn}',
                click() {
                    this.close();
                }
            }
        }
    },

    /**
     * @class A UI dialog.
     *        Mixes behaviour of {@link View} with jQuery dialog providing all its configuration capabilities.
     *        For the jQuery dialog's buttons option, the context of the callback is the myw.Dialog instance.
     *        Supports use of messages in configuration. See example below
     *        With default options, dialog is modal and includes a close button.
     *        To be used directly or subclassed.
     *        Can also be used as a factory
     * @example
     *  new myw.Dialog({title: '{:a_message_id}', contents: 'Some information or html'})
     * @example
     * myw.dialog({
     *     title: '{:a_message_id}', contents: 'Some information or html',
     *     buttons: {
     *        OK: {
     *            text : '{:ok_btn}',
     *            class: "primary-btn",
     *            click : function() { someContext.doSomething();}
     *        },
     *        Cancel: {
     *            text  : '{:cancel_btn}',
     *            class : "right",
     *            click : function() { this.close();}
     *        }
     *     }
     *  })
     * @param  {dialogOptions} options
     * @extends {View}
     * @constructs
     */
    async initialize(options) {
        myw.View.prototype.initialize.call(this, options);

        this._handleButtonOptions();

        if (myw.app.isHandheld) {
            this.options.position = { my: 'center', at: 'bottom', of: window, collision: 'fit' };
        }

        if (this.options.autoOpen) {
            await this.render();
            this.open();
        }
        $(window).resize(() => {
            this.rePosition();
        });
    },

    /**
     * Makes the dialog visible
     */
    open() {
        if (this.$el.is(':ui-dialog')) this.$el.dialog('open');
        else throw new Error('Dialog not yet initialized. Call render() before opening');
        this.rePosition();
    },

    /**
     * Resize and reposition the dialog
     */
    rePosition() {
        if (!this.$el.is(':ui-dialog')) return;
        let panelTopPos = this.$el.dialog('widget').offset().top;
        if (panelTopPos < 0) {
            //When the dialog goes off the top edge of the screen
            panelTopPos = 0;
            this.$el.dialog('widget').css({
                top: 0
            });
        }
        this.$el.css({
            'max-height': $(window).height() - panelTopPos - 110,
            'overflow-y': 'auto',
            'overflow-x': 'hidden'
        });
    },

    /**
     * Sets a new content for the dialog and renders it
     */
    async setContent(contents) {
        this.options.contents = contents;
        await this.render();
        this.rePosition();
    },

    /**
     * Creates the dialog according to the instance options and applies localisation
     */
    render() {
        //override the autoOpen options as we will deal with it ourselves
        const jQueryDialogOptions = _.extend({}, this.options, { autoOpen: false });
        this.$el
            .html(this.options.contents)
            .dialog(jQueryDialogOptions)
            .dialog('widget')
            .find('.ui-dialog-buttonset')
            .css('float', 'none'); // So the buttons can be right aligned if needed

        myw.translate(this, this.$el.dialog('widget'));
    },

    /**
     * Hides the dialog
     */
    close() {
        if (this.options.destroyOnClose) {
            this.destroy();
        } else {
            this.$el.dialog('close');
        }
    },

    /**
     * Removes the dialog from the DOM
     */
    destroy() {
        this.$el.dialog('destroy');
        this.remove();
    },

    //binds button handlers. ensures each instance has it's own copy of options and buttons option
    _handleButtonOptions() {
        if (!this.hasOwnProperty('options')) this.options = _.clone(this.options);
        const options = this.options;
        options.buttons = _.clone(options.buttons);
        const buttons = options.buttons;
        for (const buttonName in options.buttons) {
            buttons[buttonName] = _.clone(buttons[buttonName]);
            const button = buttons[buttonName];
            for (const propName in button) {
                const value = button[propName];
                if (typeof value == 'function') {
                    button[propName] = value.bind(this);
                }
            }
        }
    }
});

/**
 * Options for {@link Dialog}
 * Extends the options for {@link http://api.jqueryui.com/dialog/} and {@link http://backbonejs.org/#View-constructor} with:
 * For the buttons option, the context of the callback is the myw.Dialog instance.
 * @typedef dialogOptions
 * @property {html}     contents         Dialog body
 */

myw.dialog = options => new myw.Dialog(options);

export default myw.Dialog;
